import { useFetcherWithReset } from '@pages/Management/hooks/use-fetcher-with-reset';
import type { ErrorResponse } from '@services/types/validation-error-response';
import {
  isErrorResponse,
  isUnauthorizedErrorResponse,
  isValidationErrorResponse,
} from '@services/types/validation-error-response';
import { Form } from 'antd';
import { getReasonPhrase } from 'http-status-codes';
import { useCallback, useEffect, useState } from 'react';
import type { Fetcher, V7_FormMethod } from 'react-router-dom';
import type { FetcherSubmitOptions } from 'react-router-dom/dist/dom';

type JsonArray = JsonPrimitive[] | readonly JsonPrimitive[];
type JsonPrimitive = string | number | boolean | null;
type JsonObject = Record<string, JsonPrimitive | JsonArray> &
  Partial<Record<string, JsonPrimitive | JsonArray | undefined>>;

type Destructor = () => void;

function isFetcherLoading(state: Fetcher['state']) {
  return state !== 'idle';
}

export const mapEmptyToNull = ([
  key,
  value,
]: [
  string,
  unknown,
]) => [key, value === '' ? null : value];

interface SuccessParams<T> {
  request: JsonObject;
  response: T | Response;
  error?: undefined;
}
interface ErrorParams {
  request: JsonObject;
  response?: undefined;
  error: ErrorResponse;
}
type OnSubmitParams<T> = SuccessParams<T> | ErrorParams;

export type OnSubmitFunction<T> = (params: OnSubmitParams<T>) => Destructor | void;
type OnSubmitCallback<TActions extends Record<string, string>, TRequest extends JsonObject, TResponse = unknown> = (
  params:
    | {
        action: TActions[keyof TActions];
        request: TRequest;
        response: TResponse | Response | undefined;
        error?: undefined;
      }
    | { action: TActions[keyof TActions]; request: TRequest; response?: undefined; error: ErrorResponse },
) => Destructor | void;

interface UseActionRequestOptions<
  TActions extends Record<string, string>,
  TRequest extends JsonObject,
  TResponse = unknown,
> {
  fetcherKey?: string;
  resetOnSuccess?: boolean;
  onSubmit?: OnSubmitCallback<TActions, TRequest, TResponse>;
}

// TODO: request and response types based on action
export function useActionRequest<
  TActions extends Record<string, string>,
  TRequest extends JsonObject,
  TResponse = unknown,
>(options?: UseActionRequestOptions<TActions, TRequest, TResponse>) {
  const fetcher = useFetcherWithReset<Response | TResponse>({ key: options?.fetcherKey });
  const { submit: rawSubmit, state, data: response, reset: rawReset } = fetcher;

  const [request, setRequest] = useState<TRequest>();
  const [action, setAction] = useState<TActions[keyof TActions]>();
  const [error, setError] = useState<string>();

  const isLoading = state === 'loading';
  const isSubmitting = state === 'submitting';
  const isIdle = state === 'idle';
  const isLoadingOrSubmitting = isLoading || isSubmitting;
  const onSubmitCallback = options?.onSubmit;

  const submit = useCallback(
    (action: TActions[keyof TActions], data: TRequest, options?: FetcherSubmitOptions): void => {
      setRequest(data);
      setAction(action);
      options =
        options !== undefined
          ? Object.assign<FetcherSubmitOptions, FetcherSubmitOptions>({ encType: 'application/json' }, options)
          : { encType: 'application/json' };
      rawSubmit(
        {
          intent: action,
          ...data,
        },
        options,
      );
    },
    [rawSubmit],
  );

  const reset = useCallback<typeof rawReset>(() => {
    rawReset();
    setRequest(undefined);
    setAction(undefined);
    setError(undefined);
  }, [rawReset]);

  // process response
  useEffect(() => {
    if (state !== 'idle' || response === undefined || !request || !action) {
      return;
    }

    if (isValidationErrorResponse(response)) {
      const errors: string[] = [];
      for (const key in response.details) {
        errors.push(response.details[key].join(', '));
      }

      setError(errors.join(', '));

      const destructor = onSubmitCallback?.({ action, request, error: response });
      setRequest(undefined);
      setAction(undefined);

      return destructor;
    }

    if (isErrorResponse(response)) {
      // throw if it's not handled explicitly
      if (!onSubmitCallback) {
        throw new Error(getReasonPhrase(response.status));
      }

      setError(response.statusText);
      const destructor = onSubmitCallback?.({
        action,
        request,
        error: response,
      });
      setRequest(undefined);
      setAction(undefined);

      return destructor;
    }

    const submitParams = {
      action,
      request,
      response,
    };

    if (options?.resetOnSuccess) {
      reset();
    }

    return onSubmitCallback?.(submitParams);
  }, [
    state,
    action,
    request,
    response,
    onSubmitCallback,
    options?.resetOnSuccess,
    reset,
  ]);

  return { submit, reset, error, isLoading, isSubmitting, isIdle, isLoadingOrSubmitting, request, response };
}

// TODO: refactor to use useActionRequest hook internally
export function useForm<T extends Record<string, unknown>, F extends Partial<T>>(
  fetcherKey?: string,
  resetAfterSubmit?: boolean,
  onSubmit?: OnSubmitFunction<T>,
) {
  const [form] = Form.useForm<F>();
  const [error, setError] = useState<string>();
  const fetcher = useFetcherWithReset<Response | T>({ key: fetcherKey });
  const { submit, state, data: response, reset: resetFetcher } = fetcher;
  const [request, setRequest] = useState<JsonObject>();
  const isLoadingOrSubmitting = isFetcherLoading(state);

  const submitFunction = useCallback<typeof submit>(
    (data, options) => {
      setRequest(data as JsonObject);
      submit(data, options);
    },
    [submit],
  );

  const submitForm = useCallback(
    async (intent: string, method: V7_FormMethod, initialValues?: Partial<T>, additionalParams?: JsonObject) => {
      const values = Object.entries(await form.validateFields({ dirty: true }));
      const transformedValues = initialValues
        ? values.filter(([key, value]) => initialValues[key] !== value && value !== undefined).map(mapEmptyToNull)
        : values.map(mapEmptyToNull);

      if (transformedValues.length === 0 && additionalParams === undefined) {
        return { changedFields: {} as JsonObject };
      }

      const changedFields = Object.fromEntries(transformedValues) as JsonObject;
      const request = {
        intent,
        ...(additionalParams ?? {}),
        ...changedFields,
      };
      setRequest(request);

      submit(request, {
        method,
        encType: 'application/json',
      });

      return { changedFields };
    },
    [form, submit],
  );

  useEffect(() => {
    // before any submit
    if (response === undefined || request === undefined) {
      return;
    }

    if (isValidationErrorResponse(response)) {
      const fieldErrors: { name: string; errors: string[] }[] = [];
      const formErrors: string[] = [];
      for (const key in response.details) {
        if (form.getFieldInstance(key) !== undefined) {
          fieldErrors.push({
            name: key,
            errors: response.details[key],
          });
        } else {
          formErrors.push(response.details[key].join(', '));
        }
      }

      form.setFields(fieldErrors as Parameters<typeof form.setFields>[0]);
      setError(formErrors.join(', '));

      return onSubmit?.({
        request,
        error: response,
      });
    }

    if (isErrorResponse(response)) {
      setError(response.statusText);
      // throw if it's not handled explicitly
      if (!onSubmit && !isUnauthorizedErrorResponse(response)) {
        throw new Error(getReasonPhrase(response.status));
      }

      return onSubmit?.({
        request,
        error: response,
      });
    }

    setError(undefined);

    const submitParams: OnSubmitParams<T> = {
      request,
      response,
    };

    if (resetAfterSubmit) {
      resetFetcher();
      form.resetFields();
      setRequest(undefined);
    }

    return onSubmit?.(submitParams);
  }, [
    form,
    onSubmit,
    resetAfterSubmit,
    response,
    resetFetcher,
    request,
  ]);

  const resetError = useCallback(() => setError(undefined), []);

  return {
    form,
    error,
    isLoadingOrSubmitting,
    submitForm,
    submitFunction,
    resetFetcher,
    resetError,
  };
}

function useEditableForm<T extends Record<string, unknown>, F extends Partial<T>>(
  recordKey: keyof T | (keyof T)[],
  editableKeys: (keyof T)[],
  fetcherKey?: string,
  resetAfterSubmit?: boolean,
  onSubmit?: OnSubmitFunction<T>,
) {
  const [editingKey, setEditingKey] = useState<unknown>();
  const onInternalSubmit = useCallback<OnSubmitFunction<T>>(
    (params) => {
      if (!params.error) {
        setEditingKey(undefined);
      }

      return onSubmit?.(params);
    },
    [onSubmit],
  );
  const {
    form,
    isLoadingOrSubmitting,
    submitForm: submitFormInternal,
    error,
    submitFunction,
    resetFetcher,
  } = useForm<T, F>(fetcherKey, resetAfterSubmit, onInternalSubmit);

  const setRecordEditable = useCallback(
    (record: T) => {
      if (Array.isArray(recordKey)) {
        setEditingKey(recordKey.map((key) => record[key]).join('_'));
      } else {
        setEditingKey(record[recordKey]);
      }
      const values = editableKeys.reduce(
        (acc, key) => {
          // eslint-disable-next-line
          // @ts-ignore
          acc[key] = record[key];
          return acc;
        },
        {} as Parameters<typeof form.setFieldsValue>[0],
      );
      form.setFieldsValue(values);
    },
    [editableKeys, form, recordKey],
  );

  const isRecordEdited = (record: T) => {
    if (Array.isArray(recordKey)) {
      return recordKey.map((key) => record[key]).join('_') === editingKey;
    }

    return record[recordKey] === editingKey;
  };
  const cancelEditing = () => {
    setEditingKey(undefined);
  };

  const submitForm = useCallback(
    (record?: Partial<T>, intent?: string, additionalParams?: JsonObject, method?: V7_FormMethod) => {
      const params = record
        ? {
            ...(Array.isArray(recordKey)
              ? recordKey.reduce((acc, key) => {
                  acc[key as string] = record[key] as JsonPrimitive;
                  return acc;
                }, {} as JsonObject)
              : { [recordKey]: record[recordKey] as JsonPrimitive }),
          }
        : {};
      return submitFormInternal(
        intent ?? 'update',
        method || 'PATCH',
        record,
        record ? { ...params, ...(additionalParams ?? {}) } : (additionalParams ?? {}),
      );
    },
    [submitFormInternal, recordKey],
  );

  return {
    form,
    isLoadingOrSubmitting,
    cancelEditing,
    isRecordEdited,
    setRecordEditable,
    submitForm,
    submitFunction,
    error,
    resetFetcher,
  };
}

export default useEditableForm;
